import React, { useMemo } from "react";
import s from "./CardMetricsTooltipComponent.module.scss";
import { MdError } from "react-icons/md";
import {
  getPartnerLogo,
  isPositiveGrowth,
  stripTrailingPhrase,
  toLakhs,
} from "../../../../components/functions/utilities";
import { RootState } from "../../../../redux/store";
import { useSelector } from "react-redux";
import { RiArrowRightUpLine, RiArrowRightDownLine } from "react-icons/ri";
import moment from "moment";

export type MetricRow = {
  label: string;
  currMonth: number;
  growth?: number;
  unit?: string;
  target?: number | null;
  iconUrl?: string;
  isPositiveGrowth?: string;
  isUpArrow: boolean;
  dataPeriod?: [string, string];
};

// Incoming data item (your new shape)
export type ChannelData = {
  currMonth: number;
  prevMonth?: number;
  growth?: { positiveState?: string; value: number; type?: string } | null;
  unit?: string;
  ipId?: string;
  target?: number | null;
  targetPerc?: number | null;
  dataPeriod?: [string, string];
};

// Map of "Channel Name" -> ChannelData
export type DataMap = Record<string, ChannelData>;

export type CardMetricsTooltipComponentProps = {
  title: string;
  totalDisplay: string;
  data: DataMap;
  showShare?: boolean;
  formatValue?: (n: number, unit?: string) => string;
  dataPeriod?: any;
};

const defaultFormat: CardMetricsTooltipComponentProps["formatValue"] = (
  n,
  unit
) => `${unit ?? ""}${n.toLocaleString("en-IN")}`;

export default function CardMetricsTooltipComponent({
  title,
  totalDisplay,
  data,
  showShare = true,
  formatValue = defaultFormat,
  dataPeriod,
}: CardMetricsTooltipComponentProps) {

  const partnerLogo = useSelector((state: RootState) => state);
  const getLogo = (ipId: string) => {
    return getPartnerLogo(partnerLogo, ipId);
  };

  function getDataAvailabilityMessage(
    channelDataPeriod: [string, string],
    mainDataPeriod: [string, string]
  ): string | null {
    if (!channelDataPeriod?.length || channelDataPeriod.length < 2) return null;

    const [start, end] = channelDataPeriod.map((d) => moment(d, "YYYY-MM-DD"));
    const [mainStart, mainEnd] = mainDataPeriod.map((d) =>
      moment(d, "DD-MM-YYYY")
    );

    // If it exactly matches the main data period, no message
    if (start.isSame(mainStart, "day") && end.isSame(mainEnd, "day")) {
      return null;
    }

    // Format depending on month/year range
    const sameYear = start.year() === end.year();
    const sameMonth = sameYear && start.month() === end.month();

    let formattedRange = "";
    if (sameMonth) {
      formattedRange = `${start.format("MMM D")} – ${end.format(
        "MMM D, YYYY"
      )}`;
    } else if (sameYear) {
      formattedRange = `${start.format("MMM D")} – ${end.format(
        "MMM D, YYYY"
      )}`;
    } else {
      formattedRange = `${start.format("MMM D, YYYY")} – ${end.format(
        "MMM D, YYYY"
      )}`;
    }

    return `Data available: ${formattedRange}`;
  }

  const dataTill = getDataAvailabilityMessage(
    ["03-10-2025", "06-10-2025"],
    ["01-10-2025", "06-10-2025"]
  );

  console.log("data in tooltip", data);
  console.log("dataPeriod in tooltip", dataPeriod);
  const rows: MetricRow[] = useMemo(
    () =>
      Object.entries(data || {}).map(([label, v]) => ({
        label,
        currMonth: Number(v?.currMonth),
        growth:
          v?.growth &&
            typeof v.growth.value === "number" &&
            v.growth.value !== 0
            ? v.growth.value
            : undefined,
        isPositiveGrowth:
          v?.growth?.type === "NEUTRAL"
            ? "neutral"
            : isPositiveGrowth(v?.growth)
              ? "green"
              : "red",
        unit: v?.unit,
        target: v?.target ?? null,
        iconUrl: getLogo(v?.ipId),
        isUpArrow: v?.growth?.type === "INCREASE",
        dataPeriod: v?.dataPeriod,
      })),
    [data]
  );

  const total = useMemo(
    () =>
      rows.reduce(
        (acc, r) => acc + (r.target || r.target === 0 ? r.currMonth : 0 || 0),
        0
      ),
    [rows]
  );

  return (
    <div className={s.card}>
      {/* Header */}
      <div className={s.head}>
        <div>{title}</div>
        <div>₹{totalDisplay}</div>
      </div>
      <div className={s.divider} />

      {/* Rows */}
      {rows.map((r, index) => {
        const share =
          showShare && total > 0
            ? `(${Math.round((r.currMonth / total) * 100)}%)`
            : "";

        return (
          <div key={r.label} className={s.row}>
            <div className={s.left}>
              <div className={s.leftFirst}>
                {r.iconUrl ? (
                  <img className={s.icon} src={r.iconUrl} alt="" />
                ) : null}
                <span className={s.label}>
                  {stripTrailingPhrase(
                    r.label,
                    title == "Sales" ? "Sales" : "Ad Spends"
                  )}
                </span>
              </div>
              {r.target === undefined || r.target === null ? null : (
                <div className={s.note}>
                  {getDataAvailabilityMessage(r?.dataPeriod, dataPeriod)}
                </div>
              )}
            </div>

            <div className={s.right}>
              {r.target === undefined || r.target === null ? (
                <div className={s.warn}>
                  <MdError />
                  <span className={s.targetmiss}>Target missing</span>
                </div>
              ) : (
                <>
                  <div className={s.value}>
                    ₹{toLakhs(r.currMonth)}
                    {" lakhs "}
                    {share && <span className={s.subtle}>{share}</span>}
                  </div>
                  {
                    <div
                      className={
                        r.isPositiveGrowth === "neutral"
                          ? s.neutral
                          : r.isPositiveGrowth === "green"
                            ? s.up
                            : s.down
                      }
                    >
                      {r.isUpArrow ? (
                        <RiArrowRightUpLine />
                      ) : (
                        <RiArrowRightDownLine />
                      )}{" "}
                      {r.growth
                        ? Math.abs(r.growth).toLocaleString("en-IN")
                        : "-"}
                      %
                    </div>
                  }
                </>
              )}
            </div>
          </div>
        );
      })}

      {/* Footer */}
    </div>
  );
}
